import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencyEur, Globe, Buildings } from "@phosphor-icons/react";

const REGIONS = ["Europe", "Americas", "Asia"];

export default function CarbonTax() {
  const { t } = useLang();
  const [year, setYear] = useState(new Date().getFullYear());
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selected, setSelected] = useState("eu_ets");
  const [customPrice, setCustomPrice] = useState("");
  const [calc, setCalc] = useState(null);
  const [comparison, setComparison] = useState(null);

  const load = async () => {
    const [j, cmp] = await Promise.all([
      api.get("/carbon-tax/jurisdictions"),
      api.get("/carbon-tax/compare", { params: { year } }),
    ]);
    setJurisdictions(j.data);
    setComparison(cmp.data);
  };

  const compute = async () => {
    const params = { jurisdiction: selected, year };
    if (customPrice && parseFloat(customPrice) > 0) params.custom_price_eur = parseFloat(customPrice);
    const { data } = await api.get("/carbon-tax/calculate", { params });
    setCalc(data);
  };

  useEffect(() => { load(); }, [year]);
  useEffect(() => { compute(); /* eslint-disable-next-line */ }, [selected, year, customPrice]);

  const groupedByRegion = useMemo(() => {
    const g = {};
    jurisdictions.forEach((j) => {
      if (!g[j.region]) g[j.region] = [];
      g[j.region].push(j);
    });
    return g;
  }, [jurisdictions]);

  if (!calc || !comparison) return <div className="text-muted-foreground">Loading…</div>;

  const j = calc.jurisdiction;
  const totalKpi = calc.total_tax_eur;

  return (
    <div className="space-y-6" data-testid="carbon-tax-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Scenario planning</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("carbonTaxTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-2">{t("carbonTaxDesc")}</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("year")}</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-[120px] font-mono-data" data-testid="ct-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 4].map((n) => {
                  const y = new Date().getFullYear() - n;
                  return <SelectItem key={`ct-y-${y}`} value={String(y)}>{y}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Selector + KPI */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-primary/30">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Globe size={20} weight="duotone" className="text-primary" />
              {t("ctSelectJurisdiction")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("ctJurisdiction")}</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger data-testid="ct-jurisdiction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((region) => (
                      <React.Fragment key={region}>
                        <div className="px-2 py-1 text-xs font-mono-data uppercase text-muted-foreground">{region}</div>
                        {(groupedByRegion[region] || []).map((j) => (
                          <SelectItem key={j.key} value={j.key}>
                            {j.name} · {j.local_display}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("ctCustomPrice")}</Label>
                <div className="flex items-center gap-1">
                  <span className="font-mono-data text-sm text-muted-foreground">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder={String(j.price_eur_per_tco2e)}
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    className="font-mono-data"
                    data-testid="ct-custom-price"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground border-t border-border/50 pt-3">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono-data uppercase tracking-widest">
                <span>{t("ctType")}: <span className="normal-case text-foreground">{j.type}</span></span>
                <span>{t("ctScopeCoverage")}: <span className="normal-case text-foreground">{j.scope_coverage.map((s) => `Scope ${s}`).join(", ")}</span></span>
                <span>{t("ctPriceUsed")}: <span className="text-foreground">€{calc.price_used_eur_per_tco2e.toFixed(2)}/tCO₂e{calc.price_source === "custom" && " (custom)"}</span></span>
              </div>
              <div className="mt-2 text-sm">{j.notes}</div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="ct-kpi-total">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-2">
              <CurrencyEur size={14} weight="duotone" className="text-primary" />
              {t("ctEstimatedTax")}
            </div>
            <div className="font-display text-4xl font-semibold mt-2 leading-none font-mono-data">
              €{totalKpi.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-muted-foreground mt-3 font-mono-data">
              {t("ctCovered")}: {calc.covered_t_co2e.toLocaleString(undefined, { maximumFractionDigits: 3 })} tCO₂e · {t("year")} {year}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown by scope */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">{t("ctBreakdownByScope")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-2">{t("scope")}</th>
                <th className="text-right px-4 py-2">tCO₂e</th>
                <th className="text-center px-4 py-2">{t("ctApplies")}</th>
                <th className="text-right px-4 py-2">{t("ctTaxEur")}</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((s) => {
                const b = calc.breakdown[`scope${s}`];
                return (
                  <tr key={`ct-b-${s}`} className="border-b border-border/60" data-testid={`ct-breakdown-scope${s}`}>
                    <td className="px-4 py-2 font-mono-data">Scope {s}</td>
                    <td className="px-4 py-2 text-right font-mono-data">{b.t_co2e.toFixed(3)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`text-[10px] font-mono-data uppercase px-2 py-0.5 rounded-sm ${b.applies ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                        {b.applies ? t("ctYes") : t("ctNo")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono-data font-medium">
                      €{b.tax_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-secondary/50">
                <td className="px-4 py-3 font-mono-data font-semibold">{t("total")}</td>
                <td className="px-4 py-3 text-right font-mono-data font-semibold">{calc.covered_t_co2e.toFixed(3)}</td>
                <td></td>
                <td className="px-4 py-3 text-right font-mono-data font-bold text-primary">
                  €{totalKpi.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Compare all */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Buildings size={20} weight="duotone" className="text-primary" />
            {t("ctCompareAll")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2">{t("ctJurisdiction")}</th>
                  <th className="text-left px-4 py-2">{t("ctRegion")}</th>
                  <th className="text-right px-4 py-2">{t("ctPrice")}</th>
                  <th className="text-left px-4 py-2">{t("ctScopeCoverage")}</th>
                  <th className="text-right px-4 py-2">tCO₂e</th>
                  <th className="text-right px-4 py-2">{t("ctTaxEur")}</th>
                </tr>
              </thead>
              <tbody data-testid="ct-compare-table">
                {comparison.jurisdictions.map((r) => {
                  const isSel = r.key === selected;
                  return (
                    <tr key={r.key} className={`border-b border-border/60 hover:bg-secondary/40 transition-colors ${isSel ? "bg-primary/5" : ""}`}>
                      <td className="px-4 py-2">
                        <button onClick={() => setSelected(r.key)} className="text-left hover:text-primary transition-colors" data-testid={`ct-cmp-${r.key}`}>
                          {r.name}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{r.region}</td>
                      <td className="px-4 py-2 text-right font-mono-data">€{r.price_eur_per_tco2e.toFixed(2)}</td>
                      <td className="px-4 py-2 font-mono-data text-xs">{r.scope_coverage.map((s) => `S${s}`).join("+")}</td>
                      <td className="px-4 py-2 text-right font-mono-data">{r.covered_t_co2e.toFixed(2)}</td>
                      <td className={`px-4 py-2 text-right font-mono-data ${isSel ? "font-semibold text-primary" : ""}`}>
                        €{r.total_tax_eur.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
